import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import {Box, Chip, CircularProgress, InputAdornment, Paper, Stack, TextField} from "@mui/material";
import {useNavigate} from "react-router-dom";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {DataGrid} from "@mui/x-data-grid";
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import React, {useCallback, useEffect, useState} from "react";

import {api} from "../../services/apiService";
import ExportBookingsDialog from "../../components/booking/dialogs/ExportBookingsDialog";


export default function Bookings() {
    const navigate = useNavigate();
    const [statusList, setStatusList] = useState([]);
    const [filters, setFilters] = useState([]);
    const [rows, setRows] = useState([]);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [loading, setLoading] = useState(true);
    const [datagridLoading, setDatagridLoading] = useState(true);
    const [totalBookings, setTotalBookings] = useState(0);
    const [query, setQuery] = useState("");
    const [exportDialogOpen, setExportDialogOpen] = useState(false);

    const columns = [
        {field: "bookingRef", headerName: "N° Réservation", flex: 1, minWidth: 150},
        {
            field: "startTime",
            headerName: "Date prévue",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) => new Date(params).toLocaleDateString('fr-FR')
        },
        {
            field: "client",
            headerName: "Nom de la personne",
            flex: 1.5,
            minWidth: 180,
            valueGetter: (params) => params.name ? params.name : 'N/A'
        },
        {
            field: "total",
            headerName: "Total TTC (€)",
            flex: 1,
            minWidth: 130,
            valueFormatter: (params) =>
                params.toLocaleString("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                }),
        },
        {
            field: "status",
            headerName: "Statut",
            flex: 1,
            minWidth: 120,
            valueGetter: (params) => {
                return params.label;
            },
            cellClassName: (params) => {
                if (params.value === "Validée") return "text-green-600";
                if (params.value === "En attente") return "text-orange-600";
                if (params.value === "En cours") return "text-blue-600";
                if (params.value === "Passée") return "text-gray-500";
                return "";
            },
        },
        {
            field: "hasGite",
            headerName: "Gîte",
            flex: 0.7,
            minWidth: 80,
            renderCell: (params) =>
                params.value ? (
                    <CheckIcon sx={{height: "100%", color: "green"}}/>
                ) : (
                    <ClearIcon sx={{height: "100%", color: "red"}}/>
                )
        },
        {
            field: "hasSalle",
            headerName: "Salle",
            flex: 0.7,
            minWidth: 80,
            renderCell: (params) =>
                params.value ? (
                    <CheckIcon sx={{height: "100%", color: "green"}}/>
                ) : (
                    <ClearIcon sx={{height: "100%", color: "red"}}/>
                )
        },
        {
            field: "hasChambres",
            headerName: "Chambres",
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) =>
                params.value ? (
                    <CheckIcon sx={{height: "100%", color: "green"}}/>
                ) : (
                    <ClearIcon sx={{height: "100%", color: "red"}}/>
                )
        }
    ];

    const fetchBookings = useCallback(() => {
        setDatagridLoading(true);
        api.post("/bookings/list?page=" + (paginationModel.page + 1) + "&per_page=" + paginationModel.pageSize + (query && ("&query=" + encodeURIComponent(query))), {
            filters: filters
        })
            .then(response => {
                let bookings = response.data.bookings;
                // Pour chaque, on repasse et on ajoute une variable hasGite, hasSalle
                bookings = bookings.map(booking => ({
                    ...booking,
                    hasGite: booking.productsCategories.some(p => p.toLowerCase().includes("gîte") || p.toLowerCase().includes("gite")),
                    hasSalle: booking.productsCategories.some(p => p.toLowerCase().includes("salle")),
                    hasChambres: booking.productsCategories.some(p => p.toLowerCase().includes("chambre")),
                }));
                setRows(bookings);
                setTotalBookings(response.data.total);
                setDatagridLoading(false);
            })
            .catch(error => {
                console.error("Error fetching bookings:", error);
            });
    }, [paginationModel, query, filters]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchBookings();
        }, 200);

        return () => clearTimeout(timeout);
    }, [fetchBookings]);

    useEffect(() => {
        api.get("/bookings/status-list")
            .then(response => {
                setStatusList(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching status list:", error);
            });
    }, []);

    return (
        !loading ?
            <Container maxWidth="lg">
                <Typography variant="h1" gutterBottom>
                    Réservations
                </Typography>

                {/* Liste avec recherche + bouton */}
                <Grid
                    container
                    spacing={2}
                    sx={{display: "flex", justifyContent: "space-between", mt: "2rem"}}
                >
                    <TextField
                        sx={{width: {xs: "100%", md: "auto"}}}
                        placeholder="Rechercher une réservation..."
                        variant="outlined"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon/>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Stack
                        direction={{xs: "column", md: "row"}}
                        spacing={2}
                        sx={{alignSelf: "center", width: {xs: "100%", md: "auto"}}}
                    >
                        <Button
                            onClick={() => setExportDialogOpen(true)}
                            variant="outlined"
                            startIcon={<FileDownloadIcon/>}
                            sx={{height: "min-content"}}
                        >
                            Exporter
                        </Button>

                        <Button
                            onClick={() => navigate("/bookings/create")}
                            variant="contained"
                            startIcon={<AddIcon/>}
                            sx={{height: "min-content"}}
                        >
                            Nouvelle réservation
                        </Button>
                    </Stack>
                </Grid>

                <Stack direction="row" spacing={1} sx={{mt: "1rem", overflowX: "auto"}}>
                    {statusList.map((filter, index) => (
                        <Chip
                            key={index}
                            label={filter.label}
                            color={filters.includes(filter) ? "primary" : "default"}
                            onClick={() => {
                                if (filters.includes(filter)) {
                                    setFilters(filters.filter((f) => f !== filter));
                                } else {
                                    setFilters([...filters, filter]);
                                }
                            }}
                        />
                    ))}
                </Stack>

                {/* Tableau des réservations */}
                <Paper sx={{height: 400, width: "100%", mt: "1rem"}}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        paginationMode="server"
                        rowCount={totalBookings}
                        loading={datagridLoading}
                        initialState={{pagination: {paginationModel}}}
                        pageSizeOptions={[10, 25, 50]}
                        onRowClick={(params) => navigate(`/bookings/details/${params.id}`)}
                        sx={{
                            border: 0,
                            cursor: "pointer",
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f0f0f0",
                            },
                            backgroundColor: "#fff",
                        }}
                    />
                </Paper>

                <ExportBookingsDialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}/>
            </Container>
            :
            <Container maxWidth="lg" sx={{
                mt: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "60vh"
            }}>
                <Stack spacing={2} alignItems="center">
                    <Box>
                        <Typography variant="h6" sx={{mb: 2}}>Chargement...</Typography>
                        <Box sx={{display: "flex", justifyContent: "center"}}>
                            <CircularProgress color="primary"/>
                        </Box>
                    </Box>
                </Stack>
            </Container>
    );
}
